<template>
    <div class="lms__user_account_personal">
        <component-note
            v-if="userHasAPI"
            :class="'lms__user_account_personal_apinote'"
        >Data managed by API can not be updated in Form. Please contact your administrator to perform any updates.</component-note>
        <component-title :size="'medium'">{{ $t('lms.user.account.sidebar.personalInfo') }}</component-title>
        <form-text
            :disabled="user.isInpersonated() || userHasAPI"
            :name="'user-first-name'"
            :label="$t('lms.user.account.personal.labels.firstName')"
            :required="true"
            :v-validate="'required|max:45'"
            :data-vv-as="$t('forms.dataVvAs.firstName')"
            v-model="firstName"
        />
        <form-text
            :disabled="user.isInpersonated() || userHasAPI"
            :name="'user-last-name'"
            :label="$t('lms.user.account.personal.labels.lastName')"
            :required="true"
            :v-validate="'required|max:45'"
            :data-vv-as="$t('forms.dataVvAs.lastName')"
            v-model="lastName"
        />
        <form-email
            :disabled="user.isInpersonated() || userHasAPI"
            :name="'user-email'"
            :label="$t('lms.user.account.personal.labels.email')"
            :required="true"
            :v-validate="'required'"
            :data-vv-as="'email'"
            v-model="userEmail"
        />
        <component-custom-fields-handler
            v-if="loggedInUserProfileWorkspaceId"
            :fieldset="workspaceCustomFields"
            :values="customFields"
            :disabled="user.isInpersonated() || userLoggedInProfileHasAPI"
            @input="updateCustomField"
        ></component-custom-fields-handler>
        <form-submit
            :disabled="user.isInpersonated() || (userHasAPI && !Boolean(workspaceCustomFields.length)) || userLoggedInProfileHasAPI"
            ref="formsubmit"
            @reset="performReset"
            @submit="performSubmitValidate"
        />
        <pop-notify
            ref="popnotifyvalidation"
            :class="'pop__notify--validation'"
            :title-text="`Something's not quite right`"
            :content-text="'Please check that you have filled out all the fields in the form correctly and try again.'"
            :cancel-button-text="'Ok'"
            :submit-button-active="false"
        />
    </div>
</template>
<script>
import PopNotify from '@/components/default/shared/PopNotify'
import FormSubmit from '@/components/default/forms/FormSubmit'
import FormText from '@/components/default/forms/FormText'
import ComponentTitle from '@/components/default/shared/ComponentTitle'
import { validateBeforeRouteLeave, performReset, watchContentChangeEmit } from '@/helpers/mixins'
import { User } from '@/User'
import ComponentCustomFieldsHandler from '@/components/default/shared/ComponentCustomFieldsHandler'
import ComponentNote from '@/components/default/shared/ComponentNote'
import FormEmail from '@/components/default/forms/FormEmail'

export default {
  name: 'lms-user-account-personal',
  components: { FormEmail, ComponentNote, ComponentCustomFieldsHandler, FormText, FormSubmit, PopNotify, ComponentTitle },
  mixins: [validateBeforeRouteLeave, performReset, watchContentChangeEmit],
  data () {
    return {
      user: new User(),
      workspaceApiState: '',
      workspaceApiStateRequestPending: false
    }
  },
  computed: {
    firstName: {
      get () {
        return this.$store.getters.getLmsUserAccountPersonalFirstName
      },
      set (value) {
        this.$store.dispatch('lmsUserAccountSetPersonalFirstName', value)
      }
    },
    lastName: {
      get () {
        return this.$store.getters.getLmsUserAccountPersonalLastName
      },
      set (value) {
        this.$store.dispatch('lmsUserAccountSetPersonalLastName', value)
      }
    },
    userEmail: {
      get () {
        return this.$store.getters.getLmsUserAccountPersonalEmail
      },
      set (value) {
        this.$store.dispatch('lmsUserAccountSetPersonalEmail', value)
      }
    },
    userHasAPI () {
      return this.$store.getters.getLmsUserAccountPersonalAPI
    },

    userLoggedInProfileHasAPI () {
      return this.$store.getters.getLmsSuperadminWorkspaceUserProfileAPI
    },
    customFields: {
      get () {
        return this.$store.getters.getLmsUserAccountPersonalCustomFields
      }
    },
    workspaceCustomFields () {
      return this.$store.getters.getLmsSuperadminWorkspaceCustomFieldsData
    },
    loggedInUserProfileWorkspaceId () {
      return this.$store.getters.getLmsSuperadminWorkspaceUserProfileWorkspaceId
    },
    loggedInUserWorkspaceId () {
      return this.$store.getters.getAuthUserSecondStageTokenDataWorkspaceId
    },
    //
    rootItemName () {
      return 'account-personal'
    },
    nameActionUpdate () {
      return 'lmsUserAccountUpdate'
    },
    nameActionReset () {
      return 'lmsUserAccountReset'
    }
  },
  methods: {
    updateCustomField (data) {
      this.$store.dispatch('lmsUserAccountSetPersonalCustomField', data)
    },
    performSubmitValidate () {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.$store.dispatch(`${this.nameActionUpdate}`).then(() => {
            if (this.loggedInUserWorkspaceId) {
              this.$store.dispatch('authUserCheckHasEmptyRequiredCustomFields', this.loggedInUserWorkspaceId).then(response => {
                const allFilledIn = response ? response.allFilledIn : false
                if (allFilledIn) {
                  this.$store.dispatch('authUserSetHasEmptyRequiredCustomFields', false)
                } else {
                  this.$store.dispatch('authUserSetHasEmptyRequiredCustomFields', true)
                }
              }).finally(() => {
                this.$eventBus.$emit('stop-sending')
              })
            } else {
              this.$eventBus.$emit('stop-sending')
            }
          })
        } else {
          this.$refs.popnotifyvalidation.openNotify()
        }
      })
    },
    checkWorkspaceApiState (data) {
      if (!data) return

      this.workspaceApiState = ''
      this.workspaceApiStateRequestPending = true
      this.$store.dispatch('lmsSuperadminWorkspaceGetApiIntegrationState', data).then(response => {
        this.workspaceApiState = response
      }).finally(() => {
        this.workspaceApiStateRequestPending = false
      })
    }
  },
  mounted () {
    if (this.loggedInUserWorkspaceId) {
      this.checkWorkspaceApiState(this.loggedInUserWorkspaceId)
    }
  }
}
</script>

<style lang="scss">
    @import "~@/assets/scss/components/lms/user/account/_lmsuseraccountpersonal.scss";
</style>
